import { useAppDispatch, useAppSelector } from "@/lib/store.ts";
import { useEffect } from "react";
import {
  clearDetail,
  deleteUserDocument,
  fetchTeamMemberDetail,
  fetchUserDocuments,
  refetchTeamMember,
  setDocPage,
  setDocPageSize,
  setUploadDocumentModalOpen,
  updateTeamMember,
  uploadUserDocument,
} from "@/app/organization/settings/teammembers-detail/team-member-detail.slice.ts";
import { uuid } from "@/interfaces";
import type { MembershipRole, SettableMemberStatus } from "@shyne/shared/api";
import { toast } from "sonner";
import { useActiveOrganizationType } from "@/hooks/useActiveOrganizationType";
import { useAdministersActiveOrganization } from "@/hooks/useAdministersActiveOrganization";

export function useTeamMemberDetail(memberId: uuid | undefined) {
  const dispatch = useAppDispatch();
  const { detail, loading, error, uploadModalOpen, docLoading, docPage, docPageSize, docTotal, documents } =
    useAppSelector((s) => s.teamMemberDetail);
  const organizationType = useActiveOrganizationType();
  const administers = useAdministersActiveOrganization();

  const activeOrgId = useAppSelector((s) => s.organization.activeOrgId);
  const person = detail?.member.person;

  useEffect(() => {
    if (memberId && activeOrgId) dispatch(fetchTeamMemberDetail(memberId));

    return () => void dispatch(clearDetail());
  }, [dispatch, memberId, activeOrgId]);

  useEffect(() => {
    if (person && administers) {
      dispatch(fetchUserDocuments({ person, page: docPage, pageSize: docPageSize }));
    }
  }, [dispatch, person, administers, docPage, docPageSize]);

  const uploadDocument = async (file: File, expiresAt?: string | Date) => {
    if (person) await dispatch(uploadUserDocument({ person, file, expiresAt })).unwrap();
  };

  const handleDocumentUpload = async (file: File, expiresAt?: string | Date) => {
    try {
      await uploadDocument(file, expiresAt);
      toast.success("Document uploaded");
    } catch (e) {
      console.error(e);
      toast.error("Upload failed");
    } finally {
      dispatch(setUploadDocumentModalOpen(false));

      if (person) dispatch(fetchUserDocuments({ person, page: docPage, pageSize: docPageSize }));
    }
  };

  return {
    detail,
    loading,
    error,
    uploadModalOpen,
    docLoading,
    docPage,
    docPageSize,
    docTotal,
    documents,
    organizationType,
    administers,
    update: async (role: MembershipRole, status: SettableMemberStatus | undefined) => {
      if (!detail) return;
      try {
        await dispatch(updateTeamMember({ member: detail.member, role, status })).unwrap();
        toast.success("Saved.");
      } catch (e: any) {
        toast.error(e.message);
        dispatch(refetchTeamMember(detail.member.id));
      }
    },

    uploadDocument,
    deleteDocument: (id: uuid) => dispatch(deleteUserDocument(id)).unwrap(),
    handleDocumentUpload,

    setUploadDocumentModalOpen: (open: boolean) => dispatch(setUploadDocumentModalOpen(open)),

    setDocPage: (p: number) => dispatch(setDocPage(p)),
    setDocPageSize: (s: number) => dispatch(setDocPageSize(s)),
  };
}
